/**
 * The star battle rules, ported from `backend/apps/puzzletypes/star_battle/rules.py` (parity
 * is tested against the shared fixtures), plus the live checks only the client runs.
 *
 * Nothing here ever sees the answer key: it reads the public regions and the player's marks.
 */
import { DOT, STAR, type Cell } from "./types";

export type Issue = "stars_touch" | "star_count_mismatch";
export const ISSUES: Issue[] = ["stars_touch", "star_count_mismatch"];

export const key = (r: number, c: number): string => `${r},${c}`;
const byRowMajor = (a: Cell, b: Cell) => a[0] - b[0] || a[1] - b[1];

/** Every region id and its cells, in row-major order; ids sorted. */
export function regionCells(regions: string[]): Map<string, Cell[]> {
  const found = new Map<string, Cell[]>();
  regions.forEach((row, r) =>
    Array.from(row).forEach((id, c) => {
      const cells = found.get(id) ?? [];
      cells.push([r, c]);
      found.set(id, cells);
    }),
  );
  return new Map([...found.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

/** Whether two distinct cells touch, diagonally included. */
export function touching(a: Cell, b: Cell): boolean {
  return (
    (a[0] !== b[0] || a[1] !== b[1]) && Math.abs(a[0] - b[0]) <= 1 && Math.abs(a[1] - b[1]) <= 1
  );
}

function cellsWith(rows: string[], mark: string): Cell[] {
  const found: Cell[] = [];
  rows.forEach((row, r) => {
    for (let c = 0; c < row.length; c++) if (row[c] === mark) found.push([r, c]);
  });
  return found;
}

/** Whether a set of cells is orthogonally connected. */
export function connected(cells: Cell[]): boolean {
  if (cells.length === 0) return true;
  const pool = new Set(cells.map(([r, c]) => key(r, c)));
  const seen = new Set([key(...cells[0])]);
  const stack = [cells[0]];
  while (stack.length > 0) {
    const [r, c] = stack.pop() as Cell;
    for (const [rr, cc] of [
      [r - 1, c],
      [r + 1, c],
      [r, c - 1],
      [r, c + 1],
    ]) {
      const k = key(rr, cc);
      if (pool.has(k) && !seen.has(k)) {
        seen.add(k);
        stack.push([rr, cc]);
      }
    }
  }
  return seen.size === pool.size;
}

/** Whether `stars` stars, none touching another, fit in these cells. */
export function canHold(cells: Cell[], stars: number): boolean {
  if (stars <= 0) return true;
  if (cells.length < stars) return false;
  // A star rules out at most nine cells, so greedy placement always finds room.
  if (cells.length > 9 * (stars - 1)) return true;
  const pick = (from: number, chosen: Cell[]): boolean => {
    if (chosen.length === stars) return true;
    for (let i = from; i < cells.length; i++) {
      if (chosen.every((other) => !touching(other, cells[i])) && pick(i + 1, [...chosen, cells[i]]))
        return true;
    }
    return false;
  };
  return pick(0, []);
}

interface Line {
  id: string;
  cells: Cell[];
}

/** Rows, then columns, then regions (ids sorted), each named the way the feedback names it. */
export function lines(regions: string[], size: number): Line[] {
  const all: Line[] = [];
  for (let r = 0; r < size; r++)
    all.push({ id: `row:${r}`, cells: Array.from({ length: size }, (_, c) => [r, c] as Cell) });
  for (let c = 0; c < size; c++)
    all.push({ id: `col:${c}`, cells: Array.from({ length: size }, (_, r) => [r, c] as Cell) });
  for (const [id, cells] of regionCells(regions)) all.push({ id: `region:${id}`, cells });
  return all;
}

/** What an answer of the right shape breaks, in the server's order. Empty: solved. */
export function issues(regions: string[], stars: number, rows: string[]): Issue[] {
  const placed = cellsWith(rows, STAR);
  const star = new Set(placed.map(([r, c]) => key(r, c)));
  const found = new Set<Issue>();
  if (placed.some((a) => placed.some((b) => touching(a, b)))) found.add("stars_touch");
  if (
    lines(regions, rows.length).some(
      (line) => line.cells.filter(([r, c]) => star.has(key(r, c))).length !== stars,
    )
  )
    found.add("star_count_mismatch");
  return ISSUES.filter((issue) => found.has(issue));
}

export interface Contradictions {
  /** Rows, columns and regions (`row:3`, `col:0`, `region:A`) that can no longer be right. */
  lines: string[];
  /** Stars touching another star, row-major. */
  touching: Cell[];
}

/**
 * What the marks already get wrong, whatever the undecided cells become: a line with too
 * many stars, or too few stars plus undecided cells left to reach its count, and stars that
 * touch. Silent when in doubt (CLAUDE.md §9).
 */
export function contradictions(regions: string[], stars: number, marks: string[]): Contradictions {
  const placed = cellsWith(marks, STAR);
  const markAt = ([r, c]: Cell) => marks[r]?.[c];
  const flagged = lines(regions, marks.length)
    .filter((line) => {
      const count = line.cells.filter((cell) => markAt(cell) === STAR).length;
      const open = line.cells.filter(
        (cell) => markAt(cell) !== STAR && markAt(cell) !== DOT,
      ).length;
      return count > stars || count + open < stars;
    })
    .map((line) => line.id);
  return {
    lines: flagged,
    touching: placed.filter((a) => placed.some((b) => touching(a, b))).sort(byRowMajor),
  };
}

/** Rows, columns and regions holding exactly their stars. */
export function doneLines(regions: string[], stars: number, marks: string[]): string[] {
  return lines(regions, marks.length)
    .filter((line) => line.cells.filter(([r, c]) => marks[r]?.[c] === STAR).length === stars)
    .map((line) => line.id);
}
